import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Coredata, Service } from 'webapp-types';
import type { IntercomCover } from 'webapp/enums/intercom';
import { isDOMLoadedInIframe } from 'webapp/utils/common';
import { loadIntercomWidget } from 'webapp/utils/intercom';

// The Intercom messenger is the support chat of free and trial accounts (ai-chat `supportChannel`). Paid accounts
// get Genie as their support chat, but still boot Intercom with its launcher hidden, so the banners, posts and
// tours sent through Intercom reach them. The server sends the boot settings only to users it allows Intercom for
// (server/controllers/app.ts); the plan is known only here, once billing has loaded. A plan change mid-session
// changes nothing until the next reload.
export default class IntercomService extends EmberService {
	@service('billing') declare billingService: Service.Billing;

	// set by the data loader from coredata
	bootData: Coredata.IntercomBoot | null = null;
	// replaced in tests
	loadWidget: (appId: string) => void = loadIntercomWidget;

	@tracked isBooted = false;
	// the plan when the page loaded (taken at boot), so a plan change mid-session switches nothing until reload
	@tracked private planAtLoad: boolean | null = null;
	// what sits over the launcher right now
	private covers = new Set<IntercomCover>();

	// Live billing only before boot: the mobile and shared pages never boot, and nothing reads it there before
	// billing has loaded
	get isFreeOrTrialAtLoad(): boolean {
		return this.planAtLoad ?? this.billingService.isFreeOrTrial;
	}

	// Whether Intercom is the account's support chat, with its launcher
	get isEligible(): boolean {
		return this.canBoot && this.isFreeOrTrialAtLoad;
	}

	private get canBoot(): boolean {
		return !!this.bootData && !isDOMLoadedInIframe();
	}

	// Called once billing has loaded, for every account
	boot(): void {
		if (this.planAtLoad === null) {
			this.planAtLoad = this.billingService.isFreeOrTrial;
		}

		const { bootData } = this;
		if (!bootData || !this.canBoot || this.isBooted) {
			return;
		}

		try {
			this.loadWidget(bootData.appId);
			window.Intercom?.('boot', {
				app_id: bootData.appId,
				user_id: bootData.userId,
				user_hash: bootData.userHash,
				email: bootData.email,
				created_at: bootData.createdAt,
				widget: { activator: '#IntercomWidgetActivator' },
				hide_default_launcher: this.hidesLauncher,
				// custom attribute for Intercom's audience rules, e.g. Fin only where Intercom is the support chat. Intercom
				// stores it per person and the last boot wins: a user switching between a free and a paid space flips it
				intercom_support_chat: this.isEligible,
			});
			this.isBooted = true;
		} catch {
			// boot runs before the initial requests resolve, so Intercom must never keep the app from loading
		}
	}

	// Only where Intercom is the support chat: a booted paid account must not get a second chat next to Genie
	show(): void {
		if (this.isEligible) {
			this.call('show');
		}
	}

	// The Genie side panel and the lineage explorer sit where the launcher is; on a free or trial plan it shows again
	// once neither does (a paid account never shows it)
	cover(by: IntercomCover): void {
		if (!this.hidesLauncher) {
			this.call('update', { hide_default_launcher: true });
		}
		this.covers.add(by);
		this.call('hide');
	}

	uncover(by: IntercomCover): void {
		if (this.covers.delete(by) && !this.hidesLauncher) {
			this.call('update', { hide_default_launcher: false });
		}
	}

	// Intercom's single-page-app contract: an `update` per page, so URL-targeted messages and tours fire
	onRouteChange(): void {
		this.call('update', { hide_default_launcher: this.hidesLauncher });
	}

	// A paid account never gets the launcher: Genie is its support chat
	private get hidesLauncher(): boolean {
		return this.covers.size > 0 || !this.isEligible;
	}

	private call(command: string, options?: unknown): void {
		if (!this.isBooted || typeof window.Intercom !== 'function') {
			return;
		}

		try {
			if (options === undefined) {
				window.Intercom(command);
			} else {
				window.Intercom(command, options);
			}
		} catch {
			// a half-loaded widget must not break the caller
		}
	}
}

declare module '@ember/service' {
	interface Registry {
		intercom: IntercomService;
	}
}
